import React, { useEffect, useState } from "react";
import { Button as ButtonUntyped, TextField as TextFieldUntyped } from "../../primary";
import styles from "./assign.module.scss";
import { Toggle } from "./toggle";
import PlusIcon from "../../../static/images/plus-icon_white.svg";
import type { DragItem, DragOverTarget, FormBuilderQuestion, FormBuilderSectionWithQuestions } from "../../../features/custom-forms";

const TextField = TextFieldUntyped as unknown as React.ComponentType<{
  id?: string;
  name?: string;
  classes?: Record<string, string>;
  type?: string;
  errors?: unknown;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
}>;

const Button = ButtonUntyped as unknown as React.ComponentType<{
  classes?: Record<string, string>;
  type?: string;
  onClick?: () => void;
  children: React.ReactNode;
}>;

interface SectionBlockProps {
  section: FormBuilderSectionWithQuestions;
  sectionIndex: number;
  renderQuestion: (
    question: FormBuilderQuestion,
    isLast: boolean,
    index: number,
    sectionIndex: number | null,
    isMandatory: boolean
  ) => React.ReactNode;
  handleSectionTitle: (title: string, sectionIndex: number) => void;
  handleSectionToggle: (sectionIndex: number) => void;
  sectionRefs: React.MutableRefObject<Array<HTMLElement | null>>;
  errors?: unknown;
  onDragOver: (e: React.DragEvent, sectionIndex: number | null, index: number) => void;
  onDrop: (sectionIndex: number | null, index: number) => void;
  onDragStart: (sectionIndex: number | null, index: number, tempId: string) => void;
  dragOverTarget: DragOverTarget;
  onAddQuestion: (sectionIndex: number) => void;
  dragItemRef: React.MutableRefObject<DragItem | null>;
}

const SectionBlock: React.FC<SectionBlockProps> = ({
  section,
  sectionIndex,
  renderQuestion,
  handleSectionTitle,
  handleSectionToggle,
  sectionRefs,
  errors,
  onDragOver,
  onDrop,
  onDragStart,
  dragOverTarget,
  onAddQuestion,
  dragItemRef,
}) => {
  const [sectionTitle, setSectionTitle] = useState(section?.sectionPlaceholder || "");

  const dragItem = dragItemRef?.current;
  const isDraggingFromSameSection = dragItem?.fromSectionIndex === sectionIndex;

  useEffect(() => {
    setSectionTitle(section?.sectionPlaceholder || "");
  }, [section?.sectionPlaceholder]);

  return (
    <div
      key={sectionIndex}
      onDragOver={(e) => onDragOver(e, sectionIndex, section.questions.length)}
      onDrop={() => onDrop(sectionIndex, section.questions.length)}
      className={styles.section_block}
      ref={(el) => (sectionRefs.current[sectionIndex] = el)}
    >
      <div className={styles.section_header}>
        <div className="">
          <TextField
            id={`section${sectionIndex}`}
            name={`section${sectionIndex}`}
            classes={{
              column: `${styles.section_heading} pb-2`,
              inputColumn: `p-0 outline`,
              inputClassName: `${styles.section_title} ${section.sectionPlaceholder ? styles.section_title_foc : ""} ${styles.fontBold}`,
            }}
            type="text"
            errors={errors}
            placeholder={`Enter section title`}
            value={sectionTitle}
            onChange={(e) => setSectionTitle(e.target.value)}
            onBlur={() => handleSectionTitle(sectionTitle, sectionIndex)}
          />
        </div>
        <Toggle checked={section?.isMandatory === 1} type="section" index={sectionIndex} onChange={handleSectionToggle} />
      </div>

      {section.questions.map((value, i) => (
        <div
          key={value.tempId}
          draggable
          onDragStart={() => onDragStart(sectionIndex, i, value.tempId as string)}
          onDragOver={(e) => onDragOver(e, sectionIndex, i)}
          onDrop={() => onDrop(sectionIndex, i)}
        >
          {dragOverTarget.sectionIndex === sectionIndex && dragOverTarget.questionIndex === i && !isDraggingFromSameSection && (
            <div className={styles.drop_placeholder}>Drop here...</div>
          )}

          {renderQuestion(value, i === section.questions.length - 1, i, sectionIndex, section?.isMandatory === 1)}
        </div>
      ))}

      {section?.questions?.length === 0 && (
        <div className={`col-12 ${styles.noSelection}`}>
          <Button classes={{ className: `btn ${styles.btn_customs} ${styles.btn_smalls}` }} type="button" onClick={() => onAddQuestion(sectionIndex)}>
            <img src={PlusIcon} alt="addQuestion" />
            Add Question
          </Button>
        </div>
      )}

      {dragOverTarget.sectionIndex === sectionIndex && dragOverTarget.questionIndex === section.questions.length && !isDraggingFromSameSection && (
        <div
          className={styles.drop_placeholder}
          onDragOver={(e) => onDragOver(e, sectionIndex, section.questions.length)}
          onDrop={() => onDrop(sectionIndex, section.questions.length)}
        >
          Drop here...
        </div>
      )}
    </div>
  );
};

export default SectionBlock;
